import { Component, Optional, Inject, Renderer2 } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { IAppState } from './app.state';
import { IVisualState } from './visual/visual.reducer';
import { ILoginState } from './login/login.reducer';
import { invalidateProfile } from './profile/profile.actions';
import { selectCurrentAttachment, selectChildrenNotesCount } from './share/models';
import { selectCurrentProfile } from './profile/profile.selector';
import { selectVisual } from './visual/visual.selector';
import { selectCurrentShare } from './share/models/share.selector';
import { selectCurrentNote, selectShareCopy } from './share/models';
import { getDialog } from './common/dialogs/dialogs.selector'
import { Config } from './universal-config';
import { requestOrganizations, cleanOrganizations } from './organizations';
import { requestWorkspaces, cleanWorkspaces } from './workspaces/workspaces.actions';
import { setIsVideo, setTheme, setIsFullWidth, setNoteFont, toggleFullscreen } from './visual/visual.actions';
import { REQUEST } from '@nguniversal/express-engine/tokens';
import { selectBranding } from './branding/branding.selector';
import { setSharesLogoStoredFileUUID, setBrandingTheme } from './branding/branding.actions';
import { Meta } from '@angular/platform-browser';
import { Meta as _Meta  } from '../angular2-meta';
import { filter, map, take } from 'rxjs/operators';
import { IBrandingState } from './branding/branding.reducer';
import { selectShareOrganization } from './organizations/organizations.selector';
import { combineLatest } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { selectAllWorkspaces } from "./workspaces/workspaces.selector";
import { MetricsService } from './metrics.service';
import { QueryService } from '../query.service';

@Component({
  selector: 'nns-app',
  host: {
    '[class]': 'classNames'
  },
  styles: [`
    .nns-app-content {position:relative;}

    nns-toasts {
      transform:translate(-50%,-50%);
      left:50%;
      top:50%;
      right:auto;
    }

    .nns-toasts__right{
      right: 20px;
      top: auto;
      left: auto;
      transform: none;
      bottom: 20px;
    }

    :host.nns-embed{
      padding-top: 0;
      padding-bottom: 52px;
    }

    :host.nns-embed:not(.nns-wordpress){
      border: 1px solid rgba(0, 0, 0, 0.12);
    }

    :host.nns-wordpress {
      overflow: hidden;
    }

    :host.nns-preview{
      overflow: hidden;
    }

    :host.nns-embed__video{
      padding-top: 0;
      overflow: hidden;
    }

    :host.nns-embed-header-hidden{
      padding-top: 0;
      padding-bottom: 0;
    }

    :host.nns-embed ::ng-deep .nedit-root{
      padding-bottom: 45px;
    }

    :host.nns-embed-header-hidden ::ng-deep .nedit-root{
      padding-bottom: 0;
    }

    :host.nns-embed__video nns-embed-header{
      margin-top: -60px;
      transition: 0.3s;
    }

    :host.nns-embed__video:hover nns-embed-header{
      margin-top: 0;
    }

    .nns-preview-inner{
      display: none;
    }

    :host.nns-preview .nns-preview-inner{
      position: absolute;
      display: block;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: 9999;
    }
  `, `
    :host.nns-theme-night {
      color: #F3F6FA;
      background: #20252B;
    }
    @media print {
      :host.nns-theme-night {
        color: #000;
      }
      :host{
        overflow: unset;
      }
      .nns-app-content{
        display: block;
      }
    }
    @media only screen and (max-width: 768px) {
      :host:not(.nns-embed) .nns-app-content:not(.nns-app-content__mobile-no-header) {
        margin-top: 57px;
      }

      :host.nns-embed .nns-app-content{
        padding-top: 0;
      }

      :host-context nns-header {
        position:fixed;
        top:0px;
        left:0px;
        width:100%;
        height:57px;
        -webkit-backface-visibility: hidden;
      }
    }
  `],
  template: `
  <nns-embed-header
    *ngIf="(visual$ | async)?.isEmbed && !(visual$ | async)?.isVideo && !(visual$ | async)?.isEmbedHeaderHidden && !(isNetworkError$ | async) && !(isNotePasswordClosed$ | async)"
    [note]="(currentNote$ | async)"
    [visual]="(visual$ | async)"
    [share]="share$ | async"
    [branding]="branding$ | async"
    ></nns-embed-header>
  <nns-header class="nns-animated" flex="none"
    *ngIf="!(isNetworkError$ | async) && !(isNotePasswordClosed$ | async)"
    [ngClass]="divClasses"
    [profile]="(profile$ | async)"
    [attachment]="(currentAttachment$ | async)"
    [login]="(login$ | async)"
    [visual]="(visual$ | async)"
    [note]="currentNote$ | async"
    [share]="share$ | async"
    [shareCopy]="shareCopy$ | async"
    [branding]="branding$ | async"
    [childrenNotesCount]="childrenNotesCount$ | async"
    [shareOrganization]="shareOrganization$ | async"
    [workspaces]="workspaces$ | async"
    [hidden]="!(
      !(visual$ | async)?.isHeaderFixed
      && !(visual$ | async)?.isEmbed
      && (branding$ | async)?.sharesTopbar)"
    ></nns-header>
  <div style="height:57px"
    *ngIf="(visual$ | async)?.isHeaderFixed && !(visual$ | async)?.isFullscreen && (!(branding$ | async) || (branding$ | async)?.sharesTopbar == true)"
  ></div>
  <div flex layout="column" class="nns-app-content" [ngClass]="{'nns-app-content__mobile-no-header': (branding$ | async) && (branding$ | async)?.sharesTopbar == false}">
    <router-outlet></router-outlet>
  </div>
  <nns-dialog
    *ngIf="(dialog$ | async)?.isShowDialog"
    [dialogOptions]="(dialog$ | async)?.dialogOptions">
    </nns-dialog>
  <nns-toasts *ngIf="(centerToasts$ | async)?.length"
    [toasts]="(centerToasts$ | async)"
  >
  </nns-toasts>
  <nns-toasts class="nns-toasts__right" *ngIf="(rightToasts$ | async)?.length"
    [toasts]="(rightToasts$ | async)"
  >
  </nns-toasts>
  <nns-note-image-viewer></nns-note-image-viewer>
  <nns-socket-io
    *ngIf="(profile$ | async)"
    [shareCopy]="shareCopy$ | async"
  ></nns-socket-io>
  <google-analytics
    [profile]="(profile$ | async)"
  ></google-analytics>
  <div class="nns-preview-inner"></div>
  `
})

export class AppComponent {
  subscription;
  dialogSubscription;
  dialogState;
  divClasses = {
    "nns-fullscreen": false
  };
  classNames = "day-theme";

  currentAttachment$;
  shareCopy$;
  share$;
  profile$;
  visual$;
  currentNote$;
  centerToasts$;
  rightToasts$;
  login$;
  dialog$;
  branding$;
  childrenNotesCount$;
  shareOrganization$;
  workspaces$;
  isNetworkError$;
  isNotePasswordClosed$;

  stopListening;
  fileServerUrl;
  isFullscreen;

  constructor(
    public _store: Store<IAppState>,
    public _config: Config,
    @Optional() @Inject(REQUEST) public request: any,
    @Inject('isBrowser') public isBrowser: boolean,
    public meta: Meta,
    public _meta: _Meta,
    private renderer: Renderer2,
    private route: ActivatedRoute,
    private metrics: MetricsService,
    private queryService: QueryService
  ) {
    this.fileServerUrl = _config.get('BOX_URL') + '/file/';
    let hasParent;

    if(isBrowser) {
      this.stopListening =
        this.renderer.listen('window', 'message', this.previewListener.bind(this));
    }

    const visual$ = this._store.select<IVisualState>('visual');
    this.subscription = visual$.subscribe((visual) => {
      const { isFullscreen, theme: appTheme, isEmbed, isVideo, isPreview, isEmbedHeaderHidden, isFullWidth, isWordpress } = visual
      this.isFullscreen = isFullscreen
      const editorTheme = appTheme === 'day' ? 'light' : 'dark';
      this.divClasses['nns-fullscreen'] = isFullscreen
      this.classNames = `nns-theme-${ appTheme } theme-${ editorTheme }`
      if(isEmbed) {
        this.classNames += ` nns-embed`;
        if(isVideo) {
          this.classNames += ` nns-embed__video`;
        }
        if (isWordpress) {
          this.classNames += ' nns-wordpress';
        }
      }
      if(isPreview) {
        this.classNames += ` nns-preview`;
      }
      if(isEmbedHeaderHidden) {
        this.classNames += ` nns-embed-header-hidden`;
      }
      if(isFullWidth) {
        this.classNames += ` nns-fullwidth`;
      }

      const customElementsTheme = appTheme === 'day' ? 'nim-theme-light' : 'nim-theme-dark'
      if (this.isBrowser) {
        document.body.className = 'nns-common  nim-theme-cyan-cyan ' + customElementsTheme;
      }
    })

    this.login$ = this._store.select<ILoginState>('login');

    this.shareCopy$ = this._store.pipe(select(selectShareCopy));
    this.share$ = this._store.pipe(select(selectCurrentShare));
    this.currentAttachment$ = this._store.pipe(select(selectCurrentAttachment));
    this.profile$ = this._store.pipe(select(selectCurrentProfile));
    this.visual$ = this._store.pipe(select(selectVisual));
    this.currentNote$ = this._store.pipe(select(selectCurrentNote));
    this.dialog$ = this._store.pipe(select(getDialog));
    this.branding$ = this._store.pipe(select(selectBranding));
    this.childrenNotesCount$ = this._store.pipe(select(selectChildrenNotesCount));
    this.shareOrganization$ = this._store.pipe(select(selectShareOrganization));
    this.workspaces$ = this._store.pipe(select(selectAllWorkspaces));
    this.isNetworkError$ = this.isNetworkError();
    this.isNotePasswordClosed$ = this.isNotePasswordClosed();


    combineLatest([this.branding$, this.visual$]).pipe(
      filter(([branding, visual]) => !branding.isFetching),
      take(1),
    )
      .subscribe(([branding, visual]) => {
        if (branding.theme && !visual.brandingThemeDisabled) {
          this._store.dispatch(setTheme(branding.theme));
        }
        if(branding && branding.sharesAllowIndexing) {
          this.meta.updateTag(
            {name: 'robots', content: 'index, follow'}
          )
        }
        this.divClasses['nns-branding-logo'] = !!branding.sharesLogoStoredFileUUID;
    })

    this.share$.subscribe((share) => {
      if(share && share.parent && share.parent.nnShareUrl) {
        hasParent = true;
      }
      if(share && share.instance && share.instance.brandingProfile) {
        this._store.dispatch(setBrandingTheme(share.instance.brandingProfile));
      }
    })

    if(this.isBrowser) {
      combineLatest(
        this.share$,
        this.visual$
      ).pipe(
        filter(([share, visual]) => share
          && share.instance
          && share.instance.htmls
          && visual
          && !visual.isEmbed
        ),
        take(1),
      ).subscribe(([share]) => {
        const nimbusHost = this._config.get('AUTH_HOST');
        const currentHost = window.location.host;
        // Custom html should work only on CNAME
        if (currentHost.includes(nimbusHost)) { return; }
        this.setCustomHtmls(share.instance.htmls);
      })
    }

    this.currentNote$.subscribe((currentNote) => {
      if(currentNote && (currentNote.role == 'video' || currentNote.role == 'screencast' || currentNote.role == 'streaming_video')) {
        this._store.dispatch(setIsVideo(true));
      } else {
        this._store.dispatch(setIsVideo(false));
      }
      if(currentNote) {
        this._store.dispatch(setIsFullWidth(currentNote.isFullwidth));
      }
      if(currentNote && currentNote.type == 'folder') {
        this._store.dispatch(setNoteFont('default'));
      }
    });

    this.centerToasts$ = this.visual$.map(
      (state) => state.toasts.filter(
        (toast) => toast.position === 'center'
      )
    );
    this.rightToasts$ = this.visual$.map(
      (state) => state.toasts.filter(
        (toast) => {
          return toast.position === 'right'
        }
      )
    );

    if (!this.queryService.isEmbed()) {
      this.profile$.subscribe((profile) => {
        if(profile) {

          this._store.dispatch(requestOrganizations())
          this._store.dispatch(requestWorkspaces())
        } else {
          this._store.dispatch(cleanOrganizations())
          this._store.dispatch(cleanWorkspaces())
        }
      })
    }

    this.route.queryParams.subscribe((queryParam) => {
      if(!queryParam.isFullscreen && this.isFullscreen) {
        this._store.dispatch(toggleFullscreen());
      }
    })
  }

  previewListener(event) {
    if (typeof event.data === 'string') {
      try {
        const params = JSON.parse(event.data);
        if (params && (params && (params.hasOwnProperty('sharesLogoStoredFileUUID')))) {
          this._store.dispatch(setSharesLogoStoredFileUUID(params.sharesLogoStoredFileUUID))
        }
      } catch (e) {
        // Do nothing, parse only branding iframe event
      }
    }
  }

  isNetworkError() {
    return this.share$.pipe(
      map((share:any) => share.networkErrorCode)
    );
  }

  isNotePasswordClosed() {
    return this.share$.pipe(
      map((share:any) => {
        const { instance } = share;
        return instance && instance.passwordRequired && !instance.accessAllowed;
      })
    );
  }

  setCustomHtmls(htmls) {
    htmls.forEach(async (html, htmlIdx) => {
      if (html.htmlHead) {
        this.addHtml(document.head, 'beforeend', html.htmlHead, htmlIdx);
        await this.evaluateCustomScripts(document.head, htmlIdx);
      }
      if (html.htmlBody) {
        const bodyPosition = html.bodyPosition == 'top' ? 'afterbegin' : 'beforeend';
        this.addHtml(document.body, bodyPosition, html.htmlBody, htmlIdx);
        this.evaluateCustomScripts(document.body, htmlIdx);
      }
    });
  }

  addHtml(targetElement: HTMLElement, position: InsertPosition, html: string, htmlIdx: number) {
    const unescape = (string = '') => string.replace(/&lt;/g, '<').replace(/&gt;/g, '>');
    const tempContainer = document.createElement('div');
    const scriptMarker = `nimbus-custom-html-${ htmlIdx }`;
    tempContainer.innerHTML = unescape(html);
    Array.from(tempContainer.querySelectorAll<HTMLScriptElement>('script'))
      .forEach(script => script.setAttribute(scriptMarker, ''));
    targetElement.insertAdjacentHTML(position, tempContainer.innerHTML);
  }

  async evaluateCustomScripts(targetElement: HTMLElement, htmlIdx) {
    const scriptMarker = `nimbus-custom-html-${ htmlIdx }`;
    const scripts = Array.from(targetElement.querySelectorAll<HTMLScriptElement>(`script[${ scriptMarker }]`));

    const selectWithEval = (idx, handler) => {
      const oldScript = scripts[idx];
      const oldContent = document.createTextNode(oldScript.innerHTML);
      const newScript = document.createElement('script');
      newScript.onload = handler;
      newScript.onerror = handler;
      Array.from(oldScript.attributes).forEach(attr => newScript.setAttribute(attr.name, attr.value));
      newScript.appendChild(oldContent);
      oldScript.parentNode.replaceChild(newScript, oldScript);
      return newScript
    }

    return new Promise((resolve) => {
      if (!scripts.length) { return resolve(true); }
      const next = (idx) => {
        const check = () => {
          if (idx === scripts.length - 1) {
            return resolve(true);
          } else {
            next(++idx);
          }
        }
        const targetScript = selectWithEval(idx, check);
        if (!targetScript.src || targetScript.hasAttribute('async')) { check(); }
      }
      next(0);
    })

  }

  ngOnInit() {
    if (window['_shareStartLoad']) {
      const sharePageLoad = Date.now() - window['_shareStartLoad'];
      this.metrics.sendTiming('share_page_load', sharePageLoad);
    }

    // if (!this.queryService.isEmbed()) {
    this._store.dispatch(invalidateProfile());
    // }
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    if (this.dialogSubscription) {
      this.dialogSubscription.unsubscribe();
    }
  }

}

export class NodeAppComponent extends AppComponent {}
